/**
 * Help articles.
 *
 * Read from `system_help_articles`, a system-wide collection rather than a
 * per-organization one. Only a sysadmin writes them.
 *
 * The owner's requirement: a user should be able to learn how to do anything
 * without asking anyone. That is why this is in v1 rather than deferred, and
 * why every page carries a `?` pointing at the article for that page.
 *
 * AUDIENCE IS A BOUNDARY. The collection also held twelve platform-operations
 * articles marked `audience: 'sysadmin'` — our Firestore paths, our
 * environment variable names, our integration key scopes — and nothing
 * enforced the field, so a customer's Viewer could read every one of them.
 * The rule in firebase/firestore.rules now refuses them, and the query below
 * is constrained to match it. The constraint is not the protection; the rule
 * is. Dropping the `where` here does not widen what a customer can read, it
 * makes the query fail, which is the failure mode we want.
 */

import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, getFirestore, query, where } from 'firebase/firestore';
import { app } from '../../lib/firebase';
import { useAuth } from '../../auth/useAuth';

const db = getFirestore(app);

/**
 * The audiences a customer may read, and therefore the values a customer
 * article is allowed to carry. `sysadmin` is deliberately absent, and so is
 * "no audience at all": an article with the field missing is not returned to
 * anybody, because absent is not the same as safe.
 */
export const CUSTOMER_AUDIENCES = [
  'all', 'viewer', 'author', 'reviewer', 'approver', 'admin',
] as const;

export interface HelpArticle {
  id: string;
  slug: string;
  title: string;
  summary: string;
  category: string;
  keywords: string[];
  /** Ordered steps, when the article describes a procedure. */
  processSteps: string[];
  /** Authored HTML. Sanitized before it is rendered — see HelpArticlePage. */
  bodyHtml: string;
}

function str(v: unknown, fallback = ''): string {
  return typeof v === 'string' && v ? v : fallback;
}

function strArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((item) => {
    if (typeof item === 'string') return [item];
    // processSteps are sometimes objects with a text field rather than plain
    // strings, depending on when the article was written.
    if (typeof item === 'object' && item !== null) {
      const text = (item as { text?: unknown; step?: unknown }).text
        ?? (item as { step?: unknown }).step;
      if (typeof text === 'string') return [text];
    }
    return [];
  });
}

function toArticle(id: string, d: Record<string, unknown>): HelpArticle {
  return {
    id,
    slug: str(d.slug, id),
    title: str(d.title, 'Untitled'),
    summary: str(d.summary),
    category: str(d.category, 'General'),
    keywords: strArray(d.keywords),
    processSteps: strArray(d.processSteps),
    bodyHtml: str(d.bodyHtml),
  };
}

/**
 * Every published article.
 *
 * Constrained by status, because the rule is a simple authenticated read and
 * an unpublished draft should not appear in a customer's help centre.
 */
export function useHelpArticles() {
  // A platform system administrator sees the platform articles too. Everybody
  // else — including an organization's own System Admin, who is a customer —
  // asks only for the audiences the rule will grant them.
  const { session } = useAuth();
  const platform = session?.platformSysAdmin === true;

  return useQuery({
    queryKey: ['help-articles', platform],
    // Help rarely changes and is read on nearly every page; an hour of cache
    // saves a round trip each time somebody opens the `?`.
    staleTime: 60 * 60 * 1000,
    queryFn: async (): Promise<HelpArticle[]> => {
      const published = where('status', '==', 'published');
      const snap = await getDocs(
        platform
          ? query(collection(db, 'system_help_articles'), published)
          : query(
              collection(db, 'system_help_articles'),
              published,
              where('audience', 'in', [...CUSTOMER_AUDIENCES]),
            ),
      );
      return snap.docs
        .map((doc) => toArticle(doc.id, doc.data()))
        .sort((a, b) => a.title.localeCompare(b.title));
    },
  });
}

/** Group articles under their category heading, categories alphabetical. */
export function byCategory(articles: readonly HelpArticle[]): [string, HelpArticle[]][] {
  const groups = new Map<string, HelpArticle[]>();
  for (const article of articles) {
    const list = groups.get(article.category) ?? [];
    list.push(article);
    groups.set(article.category, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * Search titles, summaries and keywords.
 *
 * Every word must match somewhere, the same rule the policy list follows, so
 * that "upload pdf" narrows rather than widening to everything about either.
 */
export function searchArticles(
  articles: readonly HelpArticle[],
  queryText: string,
): HelpArticle[] {
  const words = queryText.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [...articles];

  return articles.filter((a) => {
    const hay = [a.title, a.summary, a.category, ...a.keywords]
      .join('  ')
      .toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/**
 * Which article explains a given page.
 *
 * Longest matching prefix wins, so `/admin/acknowledgements/:policyId` gets
 * the acknowledgement article rather than falling back to the generic one.
 * A route with no entry gets `getting-started`, which is never wrong, only
 * unspecific.
 */
const ROUTE_HELP: [prefix: string, slug: string][] = [
  // The upload page is where a policy is ADDED. It used to point at
  // "uploading-attachments", an article about a supplementary-file feature
  // the product does not have, whose body gave the size limit as 10 MB and
  // listed seven file types that are all refused.
  ['/policies/upload', 'creating-a-policy-draft'],
  ['/policies', 'finding-policies'],
  ['/policy/', 'finding-policies'],
  ['/acknowledge/', 'acknowledgements-overview'],
  ['/my-approvals', 'approving-and-publishing'],
  ['/scope-of-service', 'scope-of-service'],
  ['/exceptions', 'exceptions-and-variances'],
  ['/competencies', 'competency-and-training'],
  ['/forms', 'forms-and-attestations'],
  ['/admin/approval-workflows', 'submitting-for-review'],
  ['/admin/acknowledgements', 'acknowledgements-overview'],
  ['/admin/overdue', 'acknowledgements-overview'],
  ['/admin/users', 'inviting-users-and-roles'],
  // Its text lives at scripts/help/importing-a-library.article.mjs. Covers the
  // attestation import at /admin/import/attestations in its last section.
  ['/admin/import', 'importing-a-library'],
  // Its text lives at scripts/help/exporting-your-data.article.mjs.
  ['/admin/export', 'exporting-your-data'],
  // The article covers all three tabs, not departments alone. Its text lives
  // at scripts/help/taxonomy.article.mjs so the page and its help can be seen
  // to disagree; the one it replaced described department owners and
  // department notifications, neither of which the product has.
  ['/admin/taxonomy', 'taxonomy'],
  ['/profile', 'getting-started'],
  ['/', 'getting-started'],
];

export function helpSlugForPath(pathname: string): string {
  const match = ROUTE_HELP
    .filter(([prefix]) => pathname === prefix || pathname.startsWith(prefix))
    .sort(([a], [b]) => b.length - a.length)[0];
  return match?.[1] ?? 'getting-started';
}
